import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mouse, Keyboard, Headphones, Webcam, Monitor, Cable, Package, ArrowRight } from 'lucide-react';

const Products = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const productCategories = [
    {
      icon: Mouse,
      name: 'Mice',
      description: 'Premium ergonomic mice for comfort and precision',
    },
    {
      icon: Keyboard,
      name: 'Keyboards',
      description: 'Professional keyboards with excellent tactile feedback',
    },
    {
      icon: Headphones,
      name: 'Headsets',
      description: 'High-quality audio with noise cancellation',
    },
    {
      icon: Webcam,
      name: 'Webcams',
      description: 'Crystal-clear video for professional meetings',
    },
    {
      icon: Monitor,
      name: 'Monitors',
      description: 'Premium displays with accurate color reproduction',
    },
    {
      icon: Cable,
      name: 'Docking Stations',
      description: 'Seamless connectivity and cable management',
    },
    {
      icon: Package,
      name: 'Mouse Pads',
      description: 'Premium surfaces for optimal tracking',
    },
    {
      icon: Package,
      name: 'Room Kits',
      description: 'Complete pre-configured setups',
    },
  ];

  return (
    <div className="bg-[#FDFBF7] pt-20">
      {/* Hero Section */}
      <section className="py-24 sm:py-32" data-testid="products-hero">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="font-['Outfit'] text-5xl sm:text-6xl tracking-tight leading-none text-[#0A1F38] mb-6">
              Premium Modular Accessories
            </h1>
            <p className="font-['Manrope'] text-base sm:text-lg leading-relaxed text-[#475569] max-w-3xl mx-auto">
              Curated products designed for easy replacement, standardization, and scaling. Perfect for
              multi-location deployments with minimal downtime.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Product Grid */}
      <section className="pb-24 sm:pb-32" data-testid="products-grid">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {productCategories.map((product, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                className="bg-white rounded-2xl p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)] border border-[#E2E8F0]/50 transition-all duration-300 hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)] group"
                data-testid={`product-${index}`}
              >
                <product.icon
                  className="text-[#C5A05B] mb-4 transition-transform duration-300 group-hover:scale-110"
                  size={40}
                  strokeWidth={1.5}
                />
                <h3 className="font-['Outfit'] text-xl font-semibold text-[#0A1F38] mb-2">{product.name}</h3>
                <p className="font-['Manrope'] text-sm leading-relaxed text-[#475569]">{product.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 sm:py-32 bg-[#F5F3ED]" data-testid="products-benefits">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <img
                src="https://images.unsplash.com/photo-1776919017122-8140e279c889"
                alt="Premium workspace accessories"
                className="rounded-3xl shadow-[0_20px_40px_rgba(0,0,0,0.08)] w-full"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="font-['Outfit'] text-4xl sm:text-5xl tracking-tight leading-tight text-[#0A1F38] mb-6">
                Why Modular Accessories?
              </h2>
              <div className="space-y-6">
                <div>
                  <h4 className="font-['Outfit'] text-xl font-semibold text-[#0A1F38] mb-2">
                    Easy Replenishment
                  </h4>
                  <p className="font-['Manrope'] text-base leading-relaxed text-[#475569]">
                    Quick replacement of worn or damaged items keeps your setups looking and performing like
                    new.
                  </p>
                </div>
                <div>
                  <h4 className="font-['Outfit'] text-xl font-semibold text-[#0A1F38] mb-2">
                    Standardized Kits
                  </h4>
                  <p className="font-['Manrope'] text-base leading-relaxed text-[#475569]">
                    Consistent quality and configuration across all locations simplifies management and
                    procurement.
                  </p>
                </div>
                <div>
                  <h4 className="font-['Outfit'] text-xl font-semibold text-[#0A1F38] mb-2">Premium Quality</h4>
                  <p className="font-['Manrope'] text-base leading-relaxed text-[#475569]">
                    Carefully selected products that balance aesthetics, performance, and durability.
                  </p>
                </div>
                <div>
                  <h4 className="font-['Outfit'] text-xl font-semibold text-[#0A1F38] mb-2">Low Downtime</h4>
                  <p className="font-['Manrope'] text-base leading-relaxed text-[#475569]">
                    Keep spare inventory on hand for immediate replacements, minimizing user disruption.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Webshop Teaser */}
      <section className="py-24 sm:py-32" data-testid="webshop-teaser">
        <div className="max-w-4xl mx-auto px-6 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-[#0A1F38] rounded-3xl p-10 sm:p-16 text-center relative overflow-hidden"
          >
            <div className="absolute top-0 left-0 w-96 h-96 bg-[#C5A05B]/10 rounded-full blur-3xl"></div>
            <div className="relative z-10">
              <h2 className="font-['Outfit'] text-4xl sm:text-5xl tracking-tight leading-tight text-[#F8FAFC] mb-6">
                Ordering Portal Coming Soon
              </h2>
              <p className="font-['Manrope'] text-base sm:text-lg leading-relaxed text-[#94A3B8] mb-10 max-w-2xl mx-auto">
                We're building a streamlined ordering platform for accessories and replacement products.
                Contact us to discuss your needs and get early access.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center px-10 py-5 rounded-full bg-[#C5A05B] text-[#0A1F38] font-['Manrope'] font-semibold text-lg transition-all duration-300 hover:bg-[#C5A05B] hover:shadow-[0_20px_40px_rgba(192,156,117,0.3)] group"
                data-testid="webshop-contact-cta"
              >
                Contact for Orders
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" size={24} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 sm:py-32 bg-[#F5F3ED]" data-testid="products-cta">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-['Outfit'] text-4xl sm:text-5xl tracking-tight leading-tight text-[#0A1F38] mb-6">
              Need Custom Product Solutions?
            </h2>
            <p className="font-['Manrope'] text-base sm:text-lg leading-relaxed text-[#475569] mb-10 max-w-2xl mx-auto">
              Whether you need complete setups or specific accessories, we'll help you find the perfect fit
              for your environment.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center px-10 py-5 rounded-full bg-[#0A1F38] text-[#FDFBF7] font-['Manrope'] font-semibold text-lg transition-all duration-300 hover:bg-[#0A1F38] hover:shadow-[0_20px_40px_rgba(0,0,0,0.15)] group"
              data-testid="products-contact-cta"
            >
              Get in Touch
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" size={24} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Products;